import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { getLeaves } from "../../../api/api";

// ─── responsive hook ────────────────────────────────────────────
function useIsMobile() {
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
  const navigate = useNavigate();

  
  useEffect(() => {

  const handlePopState = () => {

    // If no proper history then redirect home
    if (
      window.history.length <= 1
    ) {

      navigate("/", {
        replace: true,
      });
    }
  };

  window.addEventListener(
    "popstate",
    handlePopState
  );

  return () => {

    window.removeEventListener(
      "popstate",
      handlePopState
    );
  };

}, [navigate]);
  useEffect(() => {
    const handler = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener("resize", handler);
    return () => window.removeEventListener("resize", handler);
  }, []);
  return isMobile;
}

export default function HistoryTab() {
  const isMobile = useIsMobile();

  const [data,        setData]        = useState([]);
  const [loading,     setLoading]     = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 10;

  const [filters, setFilters] = useState({
    search: "", type: "All", status: "All",
  });

  useEffect(() => { setCurrentPage(1); }, [filters]);
  useEffect(() => { fetchLeaves(); },    []);

  const fetchLeaves = async () => {
    setLoading(true);
    const res = await getLeaves();
    const formatted = res
      .filter((l) => l.status !== "pending")
      .reverse()
      .map((l) => ({
        id:             l.id,
        type:
  l.leave_type?.name ||
  l.leave_type_name ||
  (l.leave_category
    ? `${l.leave_category
        .charAt(0)
        .toUpperCase()}${l.leave_category.slice(1)} Leave`
    : "General Leave"),
        from:           l.from_date,
        to:             l.to_date,
        days:           l.days,
        status:         capitalize(l.status),
        reason:         l.reason,
        managerComment: l.manager_comment || "-",
      }));
    setData(formatted);
    setLoading(false);
  };

  useEffect(() => {

  const refreshLeaves = () => {

    fetchLeaves();
  };

  window.addEventListener(
    "leave-updated",
    refreshLeaves
  );

  return () => {

    window.removeEventListener(
      "leave-updated",
      refreshLeaves
    );
  };

}, []);

  const filtered = useMemo(() => {
    return data.filter((item) => {
      const matchSearch = !filters.search || item.reason.toLowerCase().includes(filters.search.toLowerCase());
      const matchType   = filters.type   === "All" || item.type   === filters.type;
      const matchStatus = filters.status === "All" || item.status === filters.status;
      return matchSearch && matchType && matchStatus;
    });
  }, [data, filters]);

  const totalPages    = Math.ceil(filtered.length / itemsPerPage) || 1;
  const paginatedData = useMemo(() => {
    const start = (currentPage - 1) * itemsPerPage;
    return filtered.slice(start, start + itemsPerPage);
  }, [filtered, currentPage]);

  return (
    <div style={styles.container}>
      <div style={styles.title}>Leave History</div>

      {/* ── filters ── */}
      <div style={{
        ...styles.filterRow,
        flexDirection: isMobile ? "column" : "row",
      }}>
        <input
          placeholder="Search reason..."
          value={filters.search}
          onChange={(e) => setFilters({ ...filters, search: e.target.value })}
          style={{ ...styles.input, width: isMobile ? "100%" : "auto", boxSizing: "border-box" }}
        />
        <select
          value={filters.type}
          onChange={(e) => setFilters({ ...filters, type: e.target.value })}
          style={{ ...styles.input, width: isMobile ? "100%" : "auto" }}
        >
          <option>All</option>
          <option>Sick Leave</option>
          <option>Planned Leave</option>
          <option>Unplanned Leave</option>
          <option>Loss Of Pay</option>
        </select>
        <select
          value={filters.status}
          onChange={(e) => setFilters({ ...filters, status: e.target.value })}
          style={{ ...styles.input, width: isMobile ? "100%" : "auto" }}
        >
          <option>All</option>
          <option>Approved</option>
          <option>Rejected</option>
          <option>Cancelled</option>
        </select>
      </div>

      {/* ── body ── */}
      {loading ? (
        <div style={styles.center}>Loading...</div>
      ) : filtered.length === 0 ? (
        <div style={styles.center}>No history available</div>
      ) : isMobile ? (
        /* ── MOBILE: card list ── */
        <div style={{ display: "flex", flexDirection: "column", gap: "12px" }}>
          {paginatedData.map((item) => (
            <HistoryCard key={item.id} item={item} />
          ))}
        </div>
      ) : (
        /* ── DESKTOP: table ── */
        <div style={{ ...styles.card, overflowX: "auto" }}>
          <table style={styles.table}>
            <thead>
              <tr>
                <th style={styles.thLeft}>Type</th>
                <th style={styles.th}>From</th>
                <th style={styles.th}>To</th>
                <th style={styles.thLeft}>Reason</th>
                <th style={styles.th}>Days</th>
                <th style={styles.th}>Status</th>
                <th style={styles.thLeft}>Manager Comment</th>
              </tr>
            </thead>
            <tbody>
              {paginatedData.map((item) => (
                <tr key={item.id} style={styles.tr}>
                  <td style={styles.tdLeft}>{item.type}</td>
                  <td style={styles.td}>{item.from}</td>
                  <td style={styles.td}>{item.to}</td>
                  <td style={styles.tdLeft}>{item.reason}</td>
                  <td style={styles.td}>{item.days}</td>
                  <td style={styles.td}><StatusBadge status={item.status} /></td>
                  <td style={styles.tdLeft}>{item.managerComment}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* ── pagination ── */}
      <div style={styles.pagination}>
        <button
          onClick={() => setCurrentPage((p) => Math.max(p - 1, 1))}
          disabled={currentPage === 1}
          style={{ ...styles.pageBtn, opacity: currentPage === 1 ? 0.4 : 1 }}
        >◀</button>
        <span style={styles.pageIndicator}>{currentPage} / {totalPages}</span>
        <button
          onClick={() => setCurrentPage((p) => Math.min(p + 1, totalPages))}
          disabled={currentPage === totalPages}
          style={{ ...styles.pageBtn, opacity: currentPage === totalPages ? 0.4 : 1 }}
        >▶</button>
      </div>
    </div>
  );
}

// ─── HistoryCard (mobile) ─────────────────────────────────────────
function HistoryCard({ item }) {
  return (
    <div style={{
      background: "#fff",
      border: "1px solid #e5e7eb",
      borderRadius: "14px",
      padding: "16px",
      boxShadow: "0 2px 10px rgba(0,0,0,0.04)",
    }}>
      {/* type + status */}
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "10px" }}>
        <span style={{ fontWeight: "700", fontSize: "14px", color: "#111827" }}>{item.type}</span>
        <StatusBadge status={item.status} />
      </div>

      {/* dates + days */}
      <div style={{ display: "flex", gap: "16px", marginBottom: "10px", flexWrap: "wrap" }}>
        <div>
          <div style={mobileLabel}>From</div>
          <div style={mobileValue}>{item.from}</div>
        </div>
        <div style={{ borderLeft: "1px solid #e5e7eb" }} />
        <div>
          <div style={mobileLabel}>To</div>
          <div style={mobileValue}>{item.to}</div>
        </div>
        <div style={{ borderLeft: "1px solid #e5e7eb" }} />
        <div>
          <div style={mobileLabel}>Days</div>
          <div style={mobileValue}>{item.days}</div>
        </div>
      </div>

      {/* reason */}
      {item.reason && (
        <div style={{ borderTop: "1px solid #f3f4f6", paddingTop: "8px", marginTop: "4px" }}>
          <div style={mobileLabel}>Reason</div>
          <div style={{ ...mobileValue, fontWeight: "400", color: "#4b5563" }}>{item.reason}</div>
        </div>
      )}

      {/* manager comment */}
      {item.managerComment && item.managerComment !== "-" && (
        <div style={{ borderTop: "1px solid #f3f4f6", paddingTop: "8px", marginTop: "8px" }}>
          <div style={mobileLabel}>Manager Comment</div>
          <div style={{ ...mobileValue, fontWeight: "400", color: "#6b7280" }}>{item.managerComment}</div>
        </div>
      )}
    </div>
  );
}

const mobileLabel = { fontSize: "10px", color: "#94a3b8", fontWeight: "600", textTransform: "uppercase", letterSpacing: "0.4px", marginBottom: "2px" };
const mobileValue = { fontSize: "13px", color: "#111827", fontWeight: "600" };

// ─── helpers ──────────────────────────────────────────────────────
function capitalize(str) {
  return str.charAt(0).toUpperCase() + str.slice(1);
}

function StatusBadge({ status }) {
  const map = {
    Approved:  { bg: "#e6f4ea", color: "#1e7e34" },
    Rejected:  { bg: "#fdecea", color: "#c62828" },
    Cancelled: { bg: "#ececec", color: "#555"    },
    Withdrawn: { bg: "#fff7ed", color: "#9a3412" },
  };
  const { bg = "#eee", color = "#333" } = map[status] || {};
  return (
    <span style={{ ...styles.badge, background: bg, color }}>
      {status}
    </span>
  );
}

// ─── styles ───────────────────────────────────────────────────────
const styles = {
  container: { padding: "10px" },
  title: { fontSize: "16px", fontWeight: "700", marginBottom: "14px", color: "#111827" },
  filterRow: { display: "flex", gap: "10px", flexWrap: "wrap", marginBottom: "16px" },
  input: { padding: "9px 12px", border: "1px solid #d1d5db", borderRadius: "8px", fontSize: "13px", outline: "none" },
  card: { background: "#fff", border: "1px solid #ddd", borderRadius: "10px", overflow: "hidden" },
  table: { width: "100%", borderCollapse: "collapse", fontSize: "14px" },
  th:     { padding: "12px", textAlign: "center", background: "#f5f6f8", borderBottom: "1px solid #ddd" },
  thLeft: { padding: "12px", textAlign: "left",   background: "#f5f6f8", borderBottom: "1px solid #ddd" },
  tr:     { borderBottom: "1px solid #eee" },
  td:     { padding: "12px", textAlign: "center" },
  tdLeft: { padding: "12px", textAlign: "left"   },
  badge:  { padding: "4px 10px", borderRadius: "12px", fontSize: "12px", fontWeight: "600" },
  center: { textAlign: "center", padding: "30px", fontSize: "14px", color: "#6b7280" },
  pagination: {
    marginTop: "16px", display: "flex",
    justifyContent: "center", alignItems: "center", gap: "12px",
  },
  pageBtn: {
    width: "34px", height: "34px",
    borderRadius: "8px", border: "1px solid #d1d5db",
    background: "#fff", cursor: "pointer",
    display: "flex", alignItems: "center", justifyContent: "center",
    fontSize: "13px",
  },
  pageIndicator: {
    fontSize: "13px", fontWeight: "600",
    color: "#374151", minWidth: "50px", textAlign: "center",
  },
};